import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useGiftPopupsListener from "../../../hooks/useGiftPopupsListener";
import { postApiRequest } from "../../../services/postApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import SendGiftTypeModal from "../SendGiftTypeModal/SendGiftTypeModal";
import defaultAvatar from "../../../assets/icons/default.svg";
import "./GiftPopupModal.scss";

const CLOSE_DELAY_MS = 3000;
const MAX_PREVIEW_WORDS = 50;
const isVideoUrl = (url) => /\.(mp4|webm|ogg)(\?|#|$)/i.test(url || "");

const getGiftType = (popup) => popup?.giftType || popup?.gift || {};

const getPopupId = (popup) => {
  const raw =
    popup?.transactionId ||
    popup?._id ||
    popup?.id ||
    popup?.giftId ||
    popup?.popupId ||
    null;
  if (!raw) return null;
  if (typeof raw === "object") return raw._id || raw.id || raw.$oid || null;
  return String(raw);
};

const getGiftName = (giftType, language) => {
  const name = giftType?.name;
  if (!name) return "";
  if (typeof name === "string") return name;
  return language === "ar" ? name.ar || name.en : name.en || name.ar;
};

const getMediaUrl = (popup) => {
  const giftType = getGiftType(popup);
  return giftType.mediaUrl || popup?.mediaUrl || "";
};

const getThumbnailUrl = (popup) => {
  const giftType = getGiftType(popup);
  return giftType.thumbnailUrl || popup?.thumbnailUrl || "";
};

const getSender = (popup) =>
  popup?.sender || popup?.from || popup?.senderUser || popup?.user || {};

const getSenderId = (popup) => {
  const sender = getSender(popup);
  return sender?._id || popup?.senderId || popup?.fromId || null;
};

const getCategory = (popup) =>
  String(
    popup?.category ||
    popup?.giftType?.category ||
    popup?.gift?.category ||
    getGiftType(popup)?.category ||
    ""
  ).toLowerCase();

const getCustomText = (popup) => (popup?.text || "").trim();

const truncateWords = (text, max = MAX_PREVIEW_WORDS) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= max) return trimmed;
  return `${words.slice(0, max).join(" ")}…`;
};

const GiftPopupModal = () => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const isLogin = useSelector((state) => state.login.isLogin);
  const { popups, dismissCurrent } = useGiftPopupsListener(isLogin);
  const [canClose, setCanClose] = useState(false);
  const [replyCategory, setReplyCategory] = useState(null);
  const [replyReceiverId, setReplyReceiverId] = useState(null);
  const currentPopupRef = useRef(null);

  const currentPopup = popups[0];
  const isOpen = isLogin && popups.length > 0 && !!currentPopup;
  const popupKey = currentPopup?._clientId || getPopupId(currentPopup);
  currentPopupRef.current = currentPopup || null;

  useEffect(() => {
    setCanClose(false);
    if (!isOpen || !popupKey) return undefined;

    const timer = setTimeout(() => {
      setCanClose(true);
    }, CLOSE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isOpen, popupKey]);

  const acknowledgeAndDismiss = useCallback(() => {
    const popup = currentPopupRef.current;
    const transactionId = getPopupId(popup);
    dismissCurrent();

    if (!transactionId) return;
    postApiRequest(`${END_POINTS.ACKNOWLEDGE_GIFT}/${transactionId}`, {}).catch((error) => {
      console.error("Error acknowledging gift popup:", error);
    });
  }, [dismissCurrent]);

  const handleClose = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!canClose) return;
      acknowledgeAndDismiss();
    },
    [canClose, acknowledgeAndDismiss]
  );

  const handleSenderClick = useCallback(() => {
    const senderId = getSenderId(currentPopupRef.current);
    if (!senderId) return;
    acknowledgeAndDismiss();
    navigate(`/consumer-Profile/${senderId}`);
  }, [acknowledgeAndDismiss, navigate]);

  const handleReplyBack = useCallback(() => {
    const popup = currentPopupRef.current;
    const senderId = getSenderId(popup);
    const category = getCategory(popup);
    if (!senderId) return;

    acknowledgeAndDismiss();
    if (category === "roast" || category === "hype") {
      setReplyReceiverId(senderId);
      setReplyCategory(category);
      return;
    }
    navigate(`/send-gift/${senderId}`);
  }, [acknowledgeAndDismiss, navigate]);

  const handleCloseReplyModal = useCallback(() => {
    setReplyCategory(null);
    setReplyReceiverId(null);
  }, []);

  const giftType = currentPopup ? getGiftType(currentPopup) : {};
  const giftName = getGiftName(giftType, i18n.language);
  const mediaUrl = getMediaUrl(currentPopup);
  const thumbnailUrl = getThumbnailUrl(currentPopup);
  const mediaSrc = mediaUrl || thumbnailUrl;
  const mediaIsVideo = isVideoUrl(mediaUrl);
  const sender = getSender(currentPopup);
  const senderId = getSenderId(currentPopup);
  const senderName =
    sender?.userName || sender?.firstName || t("user_profile.unknown_user", "Unknown");
  const category = getCategory(currentPopup);
  const customText = truncateWords(getCustomText(currentPopup));
  const categoryLabel =
    category === "roast"
      ? t("gift.category_roast", "Roast")
      : category === "hype"
        ? t("gift.category_hype", "Hype")
        : t("gift.gift", "Gift");
  const replyLabel =
    category === "roast"
      ? t("gift.roast_back", "Roast Back")
      : category === "hype"
        ? t("gift.hype_back", "Hype Back")
        : t("gift.gift_back", "Gift Back");

  return (
    <>
      <Modal
        open={isOpen}
        footer={null}
        centered
        closable={false}
        keyboard={false}
        maskClosable={false}
        width={400}
        destroyOnClose
        className="gift-popup-modal"
        wrapClassName="gift-popup-modal-wrap"
        zIndex={1200}
      >
        {isOpen && (
          <div
            className={`gift-popup-content gift-popup-content--${category || "gift"}`}
            key={popupKey}
          >
            {canClose && (
              <button
                type="button"
                className="gift-popup-close"
                onClick={handleClose}
                aria-label={t("gift.popup_close", "Close")}
              >
                <CloseOutlined />
              </button>
            )}
            <p className="gift-popup-title">
              {t("gift.received_category_title", {
                category: categoryLabel,
                defaultValue: `You received a ${categoryLabel}!`,
              })}
            </p>

            <div className="gift-popup-sender">
              <button
                type="button"
                className="gift-popup-sender-avatar-btn"
                onClick={handleSenderClick}
                disabled={!senderId}
                aria-label={senderName}
              >
                <img
                  className="gift-popup-sender-avatar"
                  src={sender?.avatarUrl || defaultAvatar}
                  alt={senderName}
                />
              </button>
              <p className="gift-popup-sender-name">
                {t("gift.from_label", "From")}{" "}
                <button
                  type="button"
                  className="gift-popup-sender-link"
                  onClick={handleSenderClick}
                  disabled={!senderId}
                >
                  {senderName}
                </button>
              </p>
            </div>

            {mediaIsVideo ? (
              <video
                className="gift-popup-media"
                src={mediaUrl}
                poster={thumbnailUrl || undefined}
                muted
                loop
                autoPlay
                playsInline
              />
            ) : mediaSrc ? (
              <img className="gift-popup-media" src={mediaSrc} alt={giftName} />
            ) : null}

            {customText ? (
              <p className="gift-popup-message">“{customText}”</p>
            ) : null}

            <p className="gift-popup-prompt">
              {t("gift.show_back_love", "show back some love!")}
            </p>
            <button
              type="button"
              className="gift-popup-reply"
              onClick={handleReplyBack}
              disabled={!senderId}
            >
              {replyLabel}
            </button>
          </div>
        )}
      </Modal>

      <SendGiftTypeModal
        open={Boolean(replyCategory && replyReceiverId)}
        onClose={handleCloseReplyModal}
        category={replyCategory}
        receiverId={replyReceiverId}
      />
    </>
  );
};

export default memo(GiftPopupModal);
